import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'

import Mousetrap from 'components/mousetrap/Mousetrap'

import styles from './autosuggest.scss'

interface IProps {
  className?: string
  style?: React.CSSProperties
  value: string
  suggestions: string[]
  dataCy?: string
  onChange: (value: string) => void
  onSubmit?: (value: string) => void
}

interface IState {
  phrase: string // The text in the <input />
  highlightIdx: number
}

class Autosuggest extends React.Component<IProps, IState> {
  input: React.RefObject<HTMLInputElement> = React.createRef()

  constructor(props: IProps) {
    super(props)

    this.state = {
      phrase: String(props.value),
      highlightIdx: 0,
    }
  }

  focus = () => {
    this.input.current?.focus()
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { suggestions, onChange } = this.props
    const phrase = e.currentTarget.value
    const bestMatchIdx = suggestions.findIndex(s =>
      s.toUpperCase().startsWith(phrase.toUpperCase())
    )

    // If bestMatchIdx is -1, let it be -1 so that nothing gets highlighted
    this.setState({ phrase, highlightIdx: bestMatchIdx })

    onChange(suggestions[bestMatchIdx] || suggestions[0]) // Since there must always be a valid selection
  }

  handleSuggestionClick = (idx: number) => {
    const { suggestions, onChange, onSubmit } = this.props

    const suggestion = suggestions[idx]

    onChange(suggestion)
    onSubmit?.(suggestion)
  }

  handleSubmit = () => {
    const { highlightIdx } = this.state
    const { suggestions, onChange, onSubmit } = this.props

    const suggestion = suggestions[highlightIdx] || suggestions[0]

    onChange(suggestion)
    onSubmit?.(suggestion)
  }

  render() {
    const { phrase, highlightIdx } = this.state
    const { className, style, suggestions, dataCy } = this.props

    return (
      <div
        data-cy={dataCy}
        className={cn(styles.container, className)}
        style={style}
      >
        <input
          ref={this.input}
          type="text"
          className={styles.input}
          value={phrase}
          onChange={this.handleChange}
        />

        <ul className={styles.dropdown}>
          {suggestions.map((m, idx) => (
            <li
              key={m}
              data-cy="suggestion"
              className={cn(styles.match, {
                [styles.highlight]: highlightIdx === idx,
              })}
              onClick={() => this.handleSuggestionClick(idx)}
            >
              {m}
            </li>
          ))}
        </ul>

        <Mousetrap
          target={this.input}
          keys="up"
          onMatch={() =>
            this.setState({
              highlightIdx: Math.max(highlightIdx - 1, 0),
            })
          }
        />

        <Mousetrap
          target={this.input}
          keys="down"
          onMatch={() =>
            this.setState(s => ({
              highlightIdx: Math.min(
                s.highlightIdx + 1,
                suggestions.length - 1
              ),
            }))
          }
        />

        <Mousetrap
          target={this.input}
          preventDefault={false}
          stopPropagation={false}
          keys="enter"
          onMatch={this.handleSubmit}
        />
      </div>
    )
  }
}

export default observer(Autosuggest)
